import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { IndexdbStorage } from '@bcf-vanilla-ts-v1-utilities/indexdb-storage/indexdb-storage';
import { provideIndexdbStorage } from '@bcf-vanilla-ts-v1-utilities/indexdb-storage/indexdb-storage';
import {
  CAMPAIGN_IDS_WITH_EXPIRATION_DATE,
  SUPERIOR_IDS_WITH_EXPIRATION_DATE
} from '@bcf2-v1-configurators-tokens/affiliate/token';

type AffiliateDataRaw = string;

type AffiliateDataInMemory = {
  affData: AffiliateDataRaw;
  createdAt: Date;
};

const affiliateDataValidityInDays: number = 60;

enum AffiliateIdTier {
  RegularWithExpirationDate = 1,
  SuperiorWithExpirationDate = 2,
  CampaignWithExpirationDate = 3
}

export class AppAffiliateDataCatcherService {
  private _initialized: boolean = false;

  constructor(
    private _indexdbStorage: IndexdbStorage,
    private _campaignIdsWithExpirationDate?: number[],
    private _superiorIdsWithExpirationDate?: number[]
  ) {}

  public init(): void {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this._catchFromUrl();
  }

  private _catchFromUrl(): void {
    const searchParams: URLSearchParams = new URLSearchParams(document.location.search);
    if (searchParams.has('btag')) {
      void this._updateDataRaw(searchParams.get('btag') ?? undefined);
    }
  }

  private async _updateDataRaw(affiliateData: string | undefined): Promise<void> {
    if (!affiliateData) {
      return;
    }

    const affiliateDataFromStorage: AffiliateDataInMemory | undefined =
      await this._indexdbStorage.get('affiliate-data');

    if (this._superiorIdsWithExpirationDate) {
      const incomingId: number | undefined = this._parseLeadingAffiliateId(affiliateData);
      if (
        incomingId !== undefined &&
        affiliateDataFromStorage &&
        this._affiliateDataWithinValidityDays(affiliateDataFromStorage)
      ) {
        const storedId: number | undefined = this._parseLeadingAffiliateId(affiliateDataFromStorage.affData);
        if (
          storedId !== undefined &&
          this._storedIdIsCampaignOrSuperior(storedId) &&
          this._affiliateIdTier(incomingId) < this._affiliateIdTier(storedId)
        ) {
          return;
        }
      }
    } else if (this._isActiveInMemory(affiliateDataFromStorage)) {
      return;
    }

    await this._indexdbStorage.delete('affiliate-data');

    this._indexdbStorage.set('affiliate-data', {
      affData: affiliateData,
      createdAt: new Date()
    });
  }

  private _isActiveInMemory(affiliateData: AffiliateDataInMemory | undefined): boolean {
    if (!affiliateData || !this._affiliateDataWithinValidityDays(affiliateData)) {
      return false;
    }
    return Boolean(this._campaignIdsWithExpirationDate?.includes(Number(affiliateData.affData.split('_')[0])));
  }

  private _affiliateDataWithinValidityDays(affiliateData: AffiliateDataInMemory | undefined): boolean {
    if (!affiliateData) {
      return false;
    }
    const expiresAt: Date = new Date(affiliateData.createdAt);
    expiresAt.setDate(expiresAt.getDate() + affiliateDataValidityInDays);
    return Date.now() < expiresAt.getTime();
  }

  private _parseLeadingAffiliateId(raw: string): number | undefined {
    const parsed: number = Number(raw.split('_')[0]);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private _affiliateIdTier(affiliateId: number): AffiliateIdTier {
    if (this._campaignIdsWithExpirationDate?.includes(affiliateId)) {
      return AffiliateIdTier.CampaignWithExpirationDate;
    }
    if (this._superiorIdsWithExpirationDate?.includes(affiliateId)) {
      return AffiliateIdTier.SuperiorWithExpirationDate;
    }
    return AffiliateIdTier.RegularWithExpirationDate;
  }

  private _storedIdIsCampaignOrSuperior(affiliateId: number): boolean {
    return this._affiliateIdTier(affiliateId) > AffiliateIdTier.RegularWithExpirationDate;
  }
}

export function provideAppAffiliateDataCatcherService(): AppAffiliateDataCatcherService {
  return provideSingleton(
    AppAffiliateDataCatcherService,
    () =>
      new AppAffiliateDataCatcherService(
        provideIndexdbStorage(),
        provideDiTokenValue(CAMPAIGN_IDS_WITH_EXPIRATION_DATE),
        provideDiTokenValue(SUPERIOR_IDS_WITH_EXPIRATION_DATE)
      )
  );
}
